import React, {Component} from 'react'
import {APP_CONFIGS} from "../../../../../config/configs";
import PropTypes from "prop-types";
import Dropdown from "../../../../Common/Dropdown/Dropdown";
import {cloneDeep} from "lodash";
import {withTranslation} from "react-i18next";
import {refBlur, refFocus} from "../../../../../tools/tools";
import AirportsSelectorPopup from "./AirportsSelectorPopup";
import MoreAirportsBtn from "./MoreAirportsBtn";
import AirportsSwitcher from "./AirportsSwitcher";
import {updateCourse} from "../../../../../services/flightCourse/actions";
import {connect} from "react-redux";
import styles from '../../../SearchWidget.module.scss';
import {AIRPORTS_SELECTOR_TARGET} from "../../../../../services/flightCourse/errors-controller";

const retrieveCityPairs = (lang = 'en', filterFn = null) => {
    const airports = cloneDeep(APP_CONFIGS.CITY_PAIRS[lang.toLowerCase().substring(0, 2)]);
    return filterFn ? airports.filter(filterFn) : airports;
};

class AirportsSelector extends Component {

    constructor(props) {
        super(props);

        const {t, imagineMode} = props;
        const language = t('en');
        const airports = retrieveCityPairs(language, imagineMode ? a => a.airlineOperated : null);

        this.state = {
            origin: null,
            destination: null,
            origins: [...airports],
            originsFiltered: [...airports],
            destinations: [],
            destinationsFiltered: [],
            moreAirportsOriginsOpen: false,
            moreAirportsDestinationsOpen: false,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {t, course, imagineMode} = nextProps;
        const language = t('en');
        const {origin, destination} = course;
        const modifier = {};

        if (origin && origin.destinations) {
            const destinations = AirportsSelector.filterCityPairsByCodes(origin.destinations, language, imagineMode);
            modifier.destinations = [...destinations];
            modifier.destinationsFiltered = [...destinations]
        }

        if (imagineMode) {
            if (origin && !origin.airlineOperated) {
                modifier.origin = null;
                modifier.destination = null;
            }

            if (destination && !destination.airlineOperated) {
                modifier.destination = null;
            }

            modifier.origins = prevState.origins.filter(a => a.airlineOperated);
            modifier.originsFiltered = prevState.originsFiltered.filter(a => a.airlineOperated);
            modifier.destinations = Array.isArray(modifier.destinations)
                ? modifier.destinations.filter(a => a.airlineOperated)
                : prevState.destinations.filter(a => a.airlineOperated);
            modifier.destinationsFiltered = Array.isArray(modifier.destinationsFiltered)
                ? modifier.destinationsFiltered.filter(a => a.airlineOperated)
                : prevState.destinationsFiltered.filter(a => a.airlineOperated);
        } else {
            const defaultCityPairs = retrieveCityPairs(language);
            modifier.origins = [...defaultCityPairs];
            modifier.originsFiltered = [...defaultCityPairs];
            modifier.destinations = Array.isArray(modifier.destinations)
                ? modifier.destinations
                : [...defaultCityPairs];
            modifier.destinationsFiltered = Array.isArray(modifier.destinationsFiltered)
                ? modifier.destinationsFiltered
                : [...defaultCityPairs];
        }

        return {origin: origin, destination: destination, ...modifier};
    }

    canSwitchSelectors = () => this.state.origin && this.state.destination;

    switchSelectors = () => {
        const {origin, destination} = this.state;
        const {t, course, updateCourse, imagineMode} = this.props;
        const language = t('en');

        if (this.canSwitchSelectors()) {
            const destinations = AirportsSelector.filterCityPairsByCodes(destination.destinations, language, imagineMode);

            this.setState({
                origin: destination,
                destination: origin,
                destinations: [...destinations],
                destinationsFiltered: [...destinations]
            }, () => {
                updateCourse({...course, origin: destination, destination: origin});
            });
        }
    };

    originChangeHandler = (airport) => {
        const {originRef, destinationRef, imagineMode, course, updateCourse, t} = this.props;
        const language = t('en');
        const destinations = AirportsSelector.filterCityPairsByCodes(airport.destinations, language, imagineMode);

        this.setState({
            origin: airport,
            destination: null,
            destinations: [...destinations],
            destinationsFiltered: [...destinations],
            moreAirportsOriginsOpen: false
        }, () => {
            updateCourse({...course, origin: airport, destination: null});
            refBlur(originRef);
            refFocus(destinationRef);
        });
    };

    destinationChangeHandler = (airport) => {
        const {destinationRef, nextRef, course, updateCourse} = this.props;
        this.setState(
            {destination: airport, moreAirportsDestinationsOpen: false},
            () => {
                updateCourse({...course, destination: airport});
                refBlur(destinationRef);
                refFocus(nextRef);
            }
        );
    };

    static filterCityPairsByCodes = (codes = [], language='en', imagineMode = false) => {
        const cityPairs = retrieveCityPairs(language, imagineMode ? a => a.airlineOperated : null);

        if (!codes || (codes && !codes.length)) {
            return cityPairs;
        }

        return cityPairs.filter(cityPair => codes.includes(cityPair.code));
    };

    render() {
        const {
            origin, originsFiltered, destination, destinationsFiltered,
            moreAirportsOriginsOpen, moreAirportsDestinationsOpen
        } = this.state;
        const {children, originRef, destinationRef, prevRef, nextRef, course, multiCityMode, t} = this.props;

        const containerClasses = [styles.airportsSelectorContainer];
        if (multiCityMode && course.errors.some(c => c.target === AIRPORTS_SELECTOR_TARGET)) {
            containerClasses.push(styles.hasError);
        }

        return (
            <div className={containerClasses.join(' ')}>
                <div className={styles.originSelector}>
                    <Dropdown onChange={this.originChangeHandler}
                              containerRef={originRef}
                              prevRef={prevRef}
                              nextRef={destinationRef}
                              disabled={moreAirportsOriginsOpen}
                              optionsIdentifier={'code'}
                              value={origin}
                              searchable
                              styledLabelValue
                              styledLabelMaxChars={10}
                              showOptionsBeforeType={false}
                              boldTypingOccurrences
                              placeholder={t('From')}
                              stickCurrentValueOnTop
                              setKeyboardAnchorOnCurrentValueAlways
                              animated
                              optionsMax={4}
                              options={originsFiltered}>
                        <MoreAirportsBtn visible={!!originsFiltered.length}
                                         onClick={() => this.setState({moreAirportsOriginsOpen: true})}>
                            <i className={['icon-sata-en-pt', styles.allAirportsIcon].join(' ')}/>&nbsp;{t('All airports')}
                        </MoreAirportsBtn>
                    </Dropdown>
                    <AirportsSelectorPopup
                        backdropHandler={() => this.setState({moreAirportsOriginsOpen: false})}
                        airports={originsFiltered}
                        allowKeyboard={false}
                        selectAirportHandler={this.originChangeHandler}
                        open={moreAirportsOriginsOpen}/>
                </div>
                <AirportsSwitcher
                    disabled={!this.canSwitchSelectors()}
                    clickHandler={this.switchSelectors}
                    animated
                />
                <div className={styles.destinationSelector}>
                    <Dropdown onChange={this.destinationChangeHandler}
                              modifierItemsContainerClass={styles.xsRightModifier}
                              containerRef={destinationRef}
                              prevRef={originRef}
                              nextRef={nextRef}
                              disabled={moreAirportsDestinationsOpen}
                              optionsIdentifier={'code'}
                              value={destination}
                              searchable
                              styledLabelValue
                              styledLabelMaxChars={10}
                              showOptionsBeforeType={false}
                              boldTypingOccurrences
                              placeholder={t('To')}
                              stickCurrentValueOnTop
                              setKeyboardAnchorOnCurrentValueAlways
                              animated
                              optionsMax={4}
                              options={destinationsFiltered}>
                        <MoreAirportsBtn visible={!!destinationsFiltered.length}
                                         onClick={() => this.setState({moreAirportsDestinationsOpen: true})}>
                            <i className={['icon-sata-en-pt', styles.allAirportsIcon].join(' ')}/>&nbsp;{t('All airports')}
                        </MoreAirportsBtn>
                    </Dropdown>
                    <AirportsSelectorPopup
                        backdropHandler={() => this.setState({moreAirportsDestinationsOpen: false})}
                        airports={destinationsFiltered}
                        allowKeyboard={false}
                        selectAirportHandler={this.destinationChangeHandler}
                        open={moreAirportsDestinationsOpen}/>
                </div>
                {children}
            </div>
        );
    }
}

AirportsSelector.propTypes = {
    updateCourse: PropTypes.func.isRequired,
    multiCityMode: PropTypes.bool,
    imagineMode: PropTypes.bool,
    course: PropTypes.object,
    originRef: PropTypes.any,
    destinationRef: PropTypes.any,
    prevRef: PropTypes.any,
    nextRef: PropTypes.any,
    t: PropTypes.func,
};

AirportsSelector.defaultProps = {
    updateCourse: () => null,
    multiCityMode: false,
    imagineMode: false,
    course: null,
    originRef: null,
    destinationRef: null,
    prevRef: null,
    nextRef: null,
    t: str => str,
};

export default withTranslation()(connect(state => ({imagineMode: state.flightCourse.imagineMode}), {updateCourse})(AirportsSelector, false));
